import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, AlertTriangle, Archive, RotateCcw } from "lucide-react";

/**
 * Admin — Supply Price Tiers manager (iter183).
 * Fluid ladder — owner can add any number of tiers. Delete blocked if any
 * active Supply product still uses the tier.
 * Route: /admin/supply-tiers
 */

const OXBLOOD = "#4A0E17";

export default function AdminSupplyTiers() {
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [supplyCounts, setSupplyCounts] = useState({ live: 0, archived: 0 });
  const [migrating, setMigrating] = useState(false);
  // iter184 Task 4 — Bulk-assign state. Holds every Supply product (live +
  // unassigned or reassignable), the current selection, and the target tier.
  const [supplyProducts, setSupplyProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignTierId, setAssignTierId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignFilter, setAssignFilter] = useState("unassigned"); // "unassigned" | "all"

  const reload = async () => {
    try {
      const [tRes, pRes] = await Promise.all([
        api.get("/admin/supply/tiers"),
        api.get("/admin/products"),
      ]);
      setTiers(tRes.data);
      const supply = (pRes.data || []).filter((p) => (p.tier || "supply") === "supply");
      setSupplyCounts({
        live: supply.filter((p) => !p.archived).length,
        archived: supply.filter((p) => p.archived).length,
      });
      setSupplyProducts(supply.filter((p) => !p.archived));
    } catch (e) { toast.error(formatApiError(e, "Couldn't load tiers")); }
  };
  useEffect(() => { reload(); }, []);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const assignSelectionCount = selectedIds.size;

  const bulkAssign = async () => {
    if (assignSelectionCount === 0) { toast.info("Select at least one product"); return; }
    const target = assignTierId || null;
    const label = target ? (tiers.find((t) => t.id === target)?.label || "tier") : "no tier (unassign)";
    if (!window.confirm(`Assign ${assignSelectionCount} Supply product(s) to ${label}?`)) return;
    setAssigning(true);
    try {
      const { data } = await api.post("/admin/products/assign-supply-tier", {
        product_ids: Array.from(selectedIds),
        supply_tier_id: target,
      });
      toast.success(`Assigned ${data.modified} product(s) → ${label}`);
      clearSelection();
      setAssignTierId("");
      reload();
    } catch (e) { toast.error(formatApiError(e, "Assign failed")); }
    finally { setAssigning(false); }
  };

  const archiveAllSupply = async () => {
    const msg = `Archive ALL ${supplyCounts.live} live Supply-tier product(s)?\n\nThey'll disappear from the public catalog but stay in the database (order history keeps resolving names/SKUs). Use the Restore button to bring them back.\n\nType ARCHIVE to confirm:`;
    const ans = window.prompt(msg);
    if (ans !== "ARCHIVE") { toast.info("Cancelled"); return; }
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/products/archive-tier", { tier: "supply", confirm: "ARCHIVE", archived: true });
      toast.success(`Archived ${data.modified} Supply product(s)`);
      reload();
    } catch (e) { toast.error(formatApiError(e, "Archive failed")); }
    finally { setMigrating(false); }
  };

  const restoreAllSupply = async () => {
    if (!window.confirm(`Restore all ${supplyCounts.archived} archived Supply-tier product(s) back to the public catalog?`)) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/products/archive-tier", { tier: "supply", confirm: "ARCHIVE", archived: false });
      toast.success(`Restored ${data.modified} Supply product(s)`);
      reload();
    } catch (e) { toast.error(formatApiError(e, "Restore failed")); }
    finally { setMigrating(false); }
  };

  const create = async () => {
    if (!newLabel.trim() || !newPrice) { toast.error("Label + price required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/supply/tiers", { label: newLabel.trim(), price: Number(newPrice) });
      toast.success("Tier created");
      setNewLabel(""); setNewPrice("");
      reload();
    } catch (e) { toast.error(formatApiError(e, "Create failed")); }
    finally { setBusy(false); }
  };

  const patch = async (id, field, value) => {
    try {
      await api.patch(`/admin/supply/tiers/${id}`, { [field]: value });
      reload();
    } catch (e) { toast.error(formatApiError(e, "Update failed")); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete ${label}? Any product using it will need reassignment first.`)) return;
    try {
      await api.delete(`/admin/supply/tiers/${id}`);
      toast.success("Tier deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-8 py-10">
        <Link to="/admin" className="inline-block font-mono uppercase tracking-[0.22em] text-[11px] mb-6" style={{ color: OXBLOOD, opacity: 0.7 }}>← Back to admin</Link>
        <h1 className="font-sans font-bold text-[36px] leading-none tracking-[-0.01em] mb-2" style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>Supply Pricing Tiers</h1>
        <p className="text-[14px] mb-8" style={{ color: OXBLOOD, opacity: 0.7 }}>
          Fluid ladder of price tiers for the Supply catalog. Each Supply product gets assigned to one tier; every product in that tier is priced identically. Add as many as you need.
        </p>

        {/* Create form */}
        <div className="rounded-lg border p-4 mb-6 bg-white" style={{ borderColor: `${OXBLOOD}22` }}>
          <p className="font-mono uppercase tracking-[0.22em] text-[10.5px] mb-3" style={{ color: OXBLOOD, opacity: 0.7 }}>Add a tier</p>
          <div className="flex flex-col md:flex-row gap-2">
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label (e.g. $65 Tier)" className="flex-1 border rounded-sm px-3 py-2 text-[13px]" style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }} data-testid="tier-new-label" />
            <input type="number" step="0.01" min="0" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="Price ($)" className="w-32 border rounded-sm px-3 py-2 text-[13px] font-mono tabular-nums" style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }} data-testid="tier-new-price" />
            <button onClick={create} disabled={busy} className="rounded-sm px-4 py-2 text-white font-mono uppercase tracking-[0.22em] text-[11px] inline-flex items-center gap-1.5 disabled:opacity-60" style={{ background: OXBLOOD }} data-testid="tier-create-btn">
              <Plus size={13} /> {busy ? "Adding…" : "Add tier"}
            </button>
          </div>
        </div>

        {/* List */}
        <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: `${OXBLOOD}22` }} data-testid="tier-list">
          <table className="w-full">
            <thead>
              <tr style={{ background: `${OXBLOOD}08` }}>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Label</th>
                <th className="text-right px-4 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Price</th>
                <th className="text-center px-4 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tiers.length === 0 && (
                <tr><td colSpan={4} className="text-center px-4 py-6 text-[13px] italic" style={{ color: OXBLOOD, opacity: 0.55 }}>No tiers yet — add one above.</td></tr>
              )}
              {tiers.map((t) => (
                <tr key={t.id} className="border-t" style={{ borderColor: `${OXBLOOD}15` }} data-testid={`tier-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <input defaultValue={t.label} onBlur={(e) => { if (e.target.value !== t.label) patch(t.id, "label", e.target.value); }} className="w-full border rounded-sm px-2 py-1 text-[13px]" style={{ borderColor: `${OXBLOOD}20`, color: OXBLOOD }} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input type="number" step="0.01" defaultValue={t.price} onBlur={(e) => { const v = Number(e.target.value); if (v !== t.price) patch(t.id, "price", v); }} className="w-24 border rounded-sm px-2 py-1 text-[13px] font-mono tabular-nums text-right" style={{ borderColor: `${OXBLOOD}20`, color: OXBLOOD }} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => patch(t.id, "active", !t.active)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${t.active ? "" : "opacity-40"}`} style={{ background: t.active ? OXBLOOD : "#ccc" }} data-testid={`tier-active-toggle-${t.id}`}>
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${t.active ? "translate-x-5" : "translate-x-1"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(t.id, t.label)} className="rounded-sm border p-1.5" style={{ borderColor: `${OXBLOOD}40`, color: OXBLOOD }} data-testid={`tier-delete-${t.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bulk assign panel (Task 4) */}
        <div className="mt-10 rounded-lg border p-5 bg-white" style={{ borderColor: `${OXBLOOD}22` }} data-testid="supply-bulk-assign-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
            <div>
              <p className="font-mono uppercase tracking-[0.22em] text-[11px] font-bold" style={{ color: OXBLOOD }}>Bulk assign · post-migration rebuild</p>
              <p className="text-[13px] mt-1" style={{ color: OXBLOOD, opacity: 0.8 }}>
                Select N Supply products, pick a tier, click Assign. Products without a tier don't render on the public catalog.
              </p>
            </div>
            <div className="flex gap-1 rounded-full p-0.5" style={{ background: `${OXBLOOD}0d`, border: `1px solid ${OXBLOOD}22` }}>
              {[
                { key: "unassigned", label: `Unassigned · ${supplyProducts.filter((p) => !p.supply_tier_id).length}` },
                { key: "all", label: `All live · ${supplyProducts.length}` },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setAssignFilter(f.key)}
                  className="px-3 py-1.5 rounded-full font-mono uppercase tracking-[0.22em] text-[10px] transition-colors"
                  style={{
                    background: assignFilter === f.key ? OXBLOOD : "transparent",
                    color: assignFilter === f.key ? "#fff" : OXBLOOD,
                  }}
                  data-testid={`assign-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Action bar */}
          <div className="flex flex-wrap items-center gap-2 py-2 mb-2 border-y" style={{ borderColor: `${OXBLOOD}18` }}>
            <button
              onClick={() => {
                const visible = supplyProducts.filter((p) => assignFilter === "all" ? true : !p.supply_tier_id);
                setSelectedIds(new Set(visible.map((p) => p.id)));
              }}
              className="text-[11px] font-mono uppercase tracking-[0.22em] underline"
              style={{ color: OXBLOOD }}
              data-testid="assign-select-all"
            >
              Select all visible
            </button>
            {selectedIds.size > 0 && (
              <button onClick={clearSelection} className="text-[11px] font-mono uppercase tracking-[0.22em] underline" style={{ color: OXBLOOD, opacity: 0.7 }} data-testid="assign-clear">
                Clear ({selectedIds.size})
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <select
                value={assignTierId}
                onChange={(e) => setAssignTierId(e.target.value)}
                className="border rounded-sm px-3 py-1.5 text-[12px] bg-white"
                style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }}
                data-testid="assign-tier-select"
              >
                <option value="">— Assign to tier —</option>
                <option value="__CLEAR__">— Clear assignment —</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id} disabled={!t.active}>
                    {t.label} · ${t.price.toFixed(2)}{!t.active ? " (inactive)" : ""}
                  </option>
                ))}
              </select>
              <button
                onClick={() => bulkAssign(assignTierId === "__CLEAR__" ? null : assignTierId)}
                disabled={assigning || selectedIds.size === 0 || !assignTierId}
                className="rounded-sm px-4 py-1.5 text-white font-mono uppercase tracking-[0.22em] text-[11px] disabled:opacity-40"
                style={{ background: OXBLOOD }}
                data-testid="assign-btn"
              >
                {assigning ? "…" : `Assign ${selectedIds.size || ""}`}
              </button>
            </div>
          </div>

          {/* Product list */}
          <div className="max-h-[360px] overflow-y-auto rounded border" style={{ borderColor: `${OXBLOOD}18` }}>
            <table className="w-full text-[13px]">
              <thead className="sticky top-0" style={{ background: `${OXBLOOD}0a` }}>
                <tr>
                  <th className="w-8 px-3 py-2"></th>
                  <th className="text-left px-3 py-2 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.6 }}>Product</th>
                  <th className="text-left px-3 py-2 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.6 }}>SKU</th>
                  <th className="text-left px-3 py-2 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.6 }}>Current tier</th>
                </tr>
              </thead>
              <tbody>
                {supplyProducts
                  .filter((p) => assignFilter === "all" ? true : !p.supply_tier_id)
                  .sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }))
                  .map((p) => {
                    const currentTier = tiers.find((t) => t.id === p.supply_tier_id);
                    return (
                      <tr key={p.id} className="border-t" style={{ borderColor: `${OXBLOOD}12` }} data-testid={`assign-row-${p.slug}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={() => toggleSelect(p.id)}
                            className="accent-oxblood"
                            data-testid={`assign-cb-${p.slug}`}
                          />
                        </td>
                        <td className="px-3 py-2" style={{ color: OXBLOOD }}>{p.name}</td>
                        <td className="px-3 py-2 font-mono text-[11px]" style={{ color: OXBLOOD, opacity: 0.75 }}>{p.sku || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11px]" style={{ color: currentTier ? OXBLOOD : "#a83a3a" }}>
                          {currentTier ? `${currentTier.label} · $${currentTier.price.toFixed(2)}` : "⚠ Unassigned"}
                        </td>
                      </tr>
                    );
                  })}
                {supplyProducts.filter((p) => assignFilter === "all" ? true : !p.supply_tier_id).length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center italic" style={{ color: OXBLOOD, opacity: 0.5 }}>
                    {assignFilter === "unassigned" ? "Every live Supply product has a tier. 🎉" : "No live Supply products."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Danger zone — bulk archive migration (iter184 Task 3) */}
        <div className="mt-10 rounded-lg border p-5 bg-white" style={{ borderColor: `${OXBLOOD}55` }} data-testid="supply-migration-panel">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle size={18} style={{ color: OXBLOOD }} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-mono uppercase tracking-[0.22em] text-[11px] font-bold" style={{ color: OXBLOOD }}>Migration · Danger zone</p>
              <p className="text-[13px] mt-1" style={{ color: OXBLOOD, opacity: 0.85 }}>
                Bulk-archive every Supply product before flipping to the new fixed-tier catalog. Archived rows disappear from the public shelf but stay in the database — order history keeps resolving names/SKUs. Restore anytime.
              </p>
              <div className="flex gap-4 mt-2 font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: OXBLOOD, opacity: 0.75 }}>
                <span data-testid="supply-live-count">Live: <span className="tabular-nums">{supplyCounts.live}</span></span>
                <span data-testid="supply-archived-count">Archived: <span className="tabular-nums">{supplyCounts.archived}</span></span>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <button
              onClick={archiveAllSupply}
              disabled={migrating || supplyCounts.live === 0}
              className="rounded-sm px-4 py-2 text-white font-mono uppercase tracking-[0.22em] text-[11px] inline-flex items-center gap-1.5 disabled:opacity-40"
              style={{ background: OXBLOOD }}
              data-testid="archive-all-supply-btn"
            >
              <Archive size={13} /> {migrating ? "Working…" : `Archive all ${supplyCounts.live} live Supply products`}
            </button>
            {supplyCounts.archived > 0 && (
              <button
                onClick={restoreAllSupply}
                disabled={migrating}
                className="rounded-sm px-4 py-2 border font-mono uppercase tracking-[0.22em] text-[11px] inline-flex items-center gap-1.5 disabled:opacity-40 bg-white"
                style={{ borderColor: OXBLOOD, color: OXBLOOD }}
                data-testid="restore-all-supply-btn"
              >
                <RotateCcw size={13} /> Restore all {supplyCounts.archived} archived
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
